/**
 * AllergenSelector Component
 * 
 * Multi-select component for allergens with major allergens displayed first
 */

import { useState } from 'react';
import { useTranslation } from '@/core';
import { ALLERGENS, Allergen } from '../types/allergen.types';
import { Button } from '@/theme/components/ui/button';
import { Badge } from '@/theme/components/ui/badge';
import { Checkbox } from '@/theme/components/ui/checkbox';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/theme/components/ui/popover';
import { Separator } from '@/theme/components/ui/separator';
import { AlertTriangle, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AllergenSelectorProps {
  value: string[];
  onChange: (allergenIds: string[]) => void;
  error?: boolean;
  disabled?: boolean;
}

export function AllergenSelector({ value = [], onChange, error, disabled }: AllergenSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const majorAllergens = ALLERGENS.filter(a => a.isMajor);
  const otherAllergens = ALLERGENS.filter(a => !a.isMajor);
  const selectedAllergens = ALLERGENS.filter(a => value.includes(a.id));

  const handleToggle = (allergenId: string) => {
    if (value.includes(allergenId)) onChange(value.filter(id => id !== allergenId));
    else onChange([...value, allergenId]);
  };

  const handleRemove = (allergenId: string, e: React.MouseEvent) => {
    e.stopPropagation(); onChange(value.filter(id => id !== allergenId));
  };

  const handleClearAll = (e: React.MouseEvent) => { e.stopPropagation(); onChange([]); };

  // Treat per-badge X and the Clear-all as <span role="button"> instead of
  // nested <button> inside the PopoverTrigger's <Button>: button-in-button
  // is invalid HTML and Safari in particular promotes the inner button to a
  // sibling, breaking the flex grid of the trigger. role="button" + keyboard
  // handler keeps it accessible without the nesting.
  const onRoleButtonKey =
    (handler: (e: React.MouseEvent | React.KeyboardEvent) => void) =>
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        handler(e);
      }
    };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            // Fixed height + overflow-hidden so the trigger never grows
            // and pushes the surrounding form when chips are added. Long
            // selections collapse to the "+N more" badge.
            'w-full h-10 justify-between overflow-hidden',
            error && 'border-destructive',
            !value.length && 'text-muted-foreground',
          )}
        >
          <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
            {selectedAllergens.length === 0 ? (
              <span className="flex items-center gap-2 truncate">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="truncate">{t('inventory2.noAllergensSelected', 'No allergens selected')}</span>
              </span>
            ) : (
              selectedAllergens.slice(0, 3).map(allergen => (
                <Badge key={allergen.id} variant="secondary" className="flex items-center gap-1 shrink-0">
                  <span>{allergen.icon}</span>
                  <span className="max-w-[80px] truncate">{allergen.name}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('common.remove', 'Remove')}
                    className="ml-0.5 inline-flex rounded-full p-0.5 hover:bg-destructive/20 cursor-pointer"
                    onClick={(e) => handleRemove(allergen.id, e)}
                    onMouseDown={(e) => e.preventDefault()}
                    onKeyDown={onRoleButtonKey((e) => handleRemove(allergen.id, e as React.MouseEvent))}
                  >
                    <X className="h-3 w-3 hover:text-destructive" />
                  </span>
                </Badge>
              ))
            )}
            {selectedAllergens.length > 3 && (
              <Badge variant="outline" className="shrink-0">
                +{selectedAllergens.length - 3} {t('common.showMore', 'more')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {selectedAllergens.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                aria-label={t('common.clearAll', 'Clear all')}
                className="inline-flex p-0.5 rounded hover:bg-muted cursor-pointer"
                onClick={handleClearAll}
                onMouseDown={(e) => e.preventDefault()}
                onKeyDown={onRoleButtonKey(handleClearAll)}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-popover flex flex-col z-50"
        align="start"
        side="bottom"
        sideOffset={4}
        collisionPadding={24}
        // Default side=bottom is the natural one for inline forms that
        // scroll the page. When the field happens to be near the bottom
        // (or the form is inside a Dialog with overflow:auto) Radix
        // flips to top automatically via avoidCollisions — but anchored
        // to the trigger, not floating in empty grid space the way
        // forcing side="top" used to.
        avoidCollisions={true}
      >
        <div className="max-h-80 overflow-y-auto overscroll-contain p-3" style={{ scrollbarGutter: 'stable' }}>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground px-1 pb-1">{t('inventory2.majorAllergensEU', 'Major Allergens (EU 14)')}</p>
            {majorAllergens.map(allergen => <AllergenItem key={allergen.id} allergen={allergen} checked={value.includes(allergen.id)} onToggle={() => handleToggle(allergen.id)} />)}
          </div>
          <Separator className="my-3" />
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground px-1 pb-1">{t('inventory2.otherAllergens', 'Other Common Allergens')}</p>
            {otherAllergens.map(allergen => <AllergenItem key={allergen.id} allergen={allergen} checked={value.includes(allergen.id)} onToggle={() => handleToggle(allergen.id)} />)}
          </div>
        </div>
        <div className="border-t px-3 py-2 bg-muted/50 shrink-0">
          <p className="text-xs text-muted-foreground">
            {t('inventory2.allergensSelected', { count: value.length })}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AllergenItem({ allergen, checked, onToggle }: { allergen: Allergen; checked: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <label className={cn('flex items-center gap-3 px-2 py-1.5 rounded-md cursor-pointer transition-colors', 'hover:bg-accent', checked && 'bg-accent/50')}>
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <span className="text-lg">{allergen.icon}</span>
      <span className="text-sm flex-1">{allergen.name}</span>
      {allergen.isMajor && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{t('inventory2.majorLabel', 'Major')}</Badge>}
    </label>
  );
}
